/**
 * 字体管理（DESIGN.md §2.5 字体三条来源，这次把②用户导入③系统导入接上
 * 界面）。纯 props 进出，装配层（main.tsx）持有真实的 `FontLibrary`/
 * `UserFontStore`/Local Font Access 调用，同 `GradientEditorDialog.tsx`
 * 一套写法。
 *
 * 系统字体的列表跟"导入"是两步：先只拿轻量的族名列表（`onQuerySystemFonts`），
 * 用户点某一款的"导入"才真正取字节解析（`onImportSystemFont`）——一台
 * 机器可能有一两百款字体，没有人会一次性导入全部，逐个导入没有做
 * "全选/导入全部"这种一键操作，省得给自己挖内存坑。
 */
import type { JSX } from 'solid-js';
import { For, Show, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import type { FontRecord } from '../../io/fonts';
import styles from './FontManagerDialog.module.css';

const SOURCE_LABEL: Readonly<Record<FontRecord['source'], string>> = {
  builtin: '内置',
  user: '已上传',
  system: '系统导入',
};

export interface FontManagerDialogProps {
  readonly open: boolean;
  readonly fonts: readonly FontRecord[];
  readonly systemFontsAvailable: boolean;
  readonly onClose: () => void;
  /** 查询系统字体族名列表（轻量元数据，不含字节）。 */
  readonly onQuerySystemFonts: () => Promise<readonly string[]>;
  /** 真正导入某一款系统字体（取字节+解析+注册）。 */
  readonly onImportSystemFont: (family: string) => Promise<void>;
  readonly onUploadFont: (bytes: Uint8Array) => Promise<void>;
  readonly onRemove: (family: string) => void;
}

export function FontManagerDialog(props: FontManagerDialogProps): JSX.Element {
  const [systemFamilies, setSystemFamilies] = createSignal<readonly string[] | null>(null);
  const [systemQueryPending, setSystemQueryPending] = createSignal(false);
  const [importingFamily, setImportingFamily] = createSignal<string | null>(null);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  const loadedFamilies = (): Set<string> => new Set(props.fonts.map((f) => f.family));

  const runQuerySystemFonts = async (): Promise<void> => {
    setErrorMessage(null);
    setSystemQueryPending(true);
    try {
      setSystemFamilies(await props.onQuerySystemFonts());
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setSystemQueryPending(false);
    }
  };

  const importSystemFont = async (family: string): Promise<void> => {
    setErrorMessage(null);
    setImportingFamily(family);
    try {
      await props.onImportSystemFont(family);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setImportingFamily(null);
    }
  };

  const uploadFont = async (file: File): Promise<void> => {
    setErrorMessage(null);
    try {
      await props.onUploadFont(new Uint8Array(await file.arrayBuffer()));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} onClick={props.onClose} data-testid="font-manager-backdrop">
        <div
          class={styles.dialog}
          role="dialog"
          ref={(el) => (dialogRef = el)}
          aria-label="字体管理"
          data-testid="font-manager-dialog"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <h2 class={styles.title}>字体管理</h2>

          <div class={styles.fontList} data-testid="font-manager-list">
            <For each={props.fonts}>
              {(font) => (
                <div class={styles.fontRow} data-testid={`font-manager-row-${font.family}`}>
                  <span class={styles.fontName}>{font.family}</span>
                  <span class={styles.sourceBadge}>{SOURCE_LABEL[font.source]}</span>
                  <Show when={font.source !== 'builtin'}>
                    <button
                      type="button"
                      class={styles.removeButton}
                      aria-label={`移除字体 ${font.family}`}
                      data-testid={`font-manager-remove-${font.family}`}
                      onClick={() => props.onRemove(font.family)}
                    >
                      ×
                    </button>
                  </Show>
                </div>
              )}
            </For>
          </div>

          <Show when={errorMessage() !== null}>
            <p class={styles.error} data-testid="font-manager-error">
              {errorMessage()}
            </p>
          </Show>

          <div class={styles.importSection}>
            <label class={styles.uploadButton}>
              上传字体文件…
              <input
                type="file"
                class={styles.uploadInput}
                accept=".ttf,.otf,.woff,.ttc,.otc"
                data-testid="font-manager-upload-input"
                onChange={(event) => {
                  const file = event.currentTarget.files?.[0];
                  event.currentTarget.value = ''; // 允许连续上传同一个文件名
                  if (file !== undefined) void uploadFont(file);
                }}
              />
            </label>

            <Show when={props.systemFontsAvailable}>
              <button
                type="button"
                class={styles.secondary}
                data-testid="font-manager-query-system"
                disabled={systemQueryPending()}
                onClick={() => void runQuerySystemFonts()}
              >
                {systemQueryPending() ? '正在读取系统字体…' : '从系统字体导入…'}
              </button>
            </Show>
          </div>

          {/* 真实浏览器权限被拒绝时，`queryLocalFonts()` 有的实现是 reject
              （走 errorMessage 那条路径），有的（尤其自动化/无人可点确认框
              的场景）是静默返回空数组——两条路径都得给用户一个说法，
              不能让"待导入"区域看起来像是单纯没有内容可显示 */}
          <Show when={systemFamilies()?.length === 0}>
            <p class={styles.hint} data-testid="font-manager-no-system-fonts">
              没有找到可导入的系统字体——可能是没有授权访问，或者这台设备
              没有额外可用的字体。
            </p>
          </Show>

          <Show when={systemFamilies() !== null}>
            <div class={styles.systemFontList} data-testid="font-manager-system-list">
              <For each={systemFamilies()!.filter((family) => !loadedFamilies().has(family))}>
                {(family) => (
                  <div class={styles.systemFontRow} data-testid={`font-manager-system-row-${family}`}>
                    <span class={styles.fontName}>{family}</span>
                    <button
                      type="button"
                      class={styles.importButton}
                      data-testid={`font-manager-import-${family}`}
                      disabled={importingFamily() === family}
                      onClick={() => void importSystemFont(family)}
                    >
                      {importingFamily() === family ? '导入中…' : '导入'}
                    </button>
                  </div>
                )}
              </For>
            </div>
          </Show>

          <div class={styles.actions}>
            <button type="button" class={styles.primary} data-testid="font-manager-close" onClick={props.onClose}>
              关闭
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
